import * as Tooltip from '@radix-ui/react-tooltip';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Composer } from '@/components/Composer';
import { ConnectScreen } from '@/components/ConnectScreen';
import { DebugDrawer } from '@/components/debug/DebugDrawer';
import { DebugTab } from '@/components/debug/DebugTab';
import { ErrorToast } from '@/components/ErrorToast';
import { SettingsDialog } from '@/components/settings/SettingsDialog';
import { TopBar } from '@/components/TopBar';
import { Transcript } from '@/components/Transcript';
import { usePushToTalk } from '@/hooks/usePushToTalk';
import { useScreenState } from '@/hooks/useScreenState';
import { useSession } from '@/session/store';

export function App() {
  const connection = useSession((state) => state.connection);
  const deviceMode = useSession((state) => state.deviceMode);
  const error = useSession((state) => state.error);
  const dismissError = useSession((state) => state.dismissError);

  const [debugOpen, setDebugOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadTtsCatalog = useSession((state) => state.loadTtsCatalog);

  usePushToTalk(connection === 'open');
  useScreenState();

  // Load the provider/model/voice catalogue once, up front: the connect screen's voice selects need
  // it before the first sign-in, and it is a cheap single request against the configured endpoint.
  useEffect(() => {
    loadTtsCatalog();
  }, [loadTtsCatalog]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.ctrlKey && event.shiftKey && event.code === 'KeyD') {
        event.preventDefault();
        setDebugOpen((open) => !open);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // On the root element rather than in React state: the accent scale is a CSS variable, and every
  // component that uses it reads it through the cascade.
  useEffect(() => {
    document.documentElement.dataset.mode = deviceMode ?? 'DISH';
  }, [deviceMode]);

  const introducing = deviceMode === null;

  return (
    <Tooltip.Provider delayDuration={450} skipDelayDuration={200}>
      <div className="flex h-dvh overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onOpenSettings={() => setSettingsOpen(true)} />

          {introducing ? (
            <ConnectScreen />
          ) : (
            <>
              <Transcript />
              <Composer />
            </>
          )}
        </div>

        <DebugDrawer open={debugOpen} onClose={() => setDebugOpen(false)} />
      </div>

      {/* The drawer's own handle, so the log is one click from anywhere and the top bar stays about
          the conversation. */}
      <AnimatePresence>
        {!debugOpen && <DebugTab key="debug-tab" onOpen={() => setDebugOpen(true)} />}
      </AnimatePresence>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <AnimatePresence>
        {error && <ErrorToast key="error" message={error} onDismiss={dismissError} />}
      </AnimatePresence>
    </Tooltip.Provider>
  );
}
